import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { CGIService } from 'modules/promptuary/services/cgi/cgi.service';
import { Observable } from 'rxjs';

import { CacheService } from './cache.service';
import { IdWallService } from './id-wall.service';
import { StompService } from './stomp.service';
import { UserService } from './user.service';
import { WePayOutService } from './wepayout.service';
import { ZkuService } from './zku.service';

export interface AdminLoginResponse {
	token: string;
	professional: boolean;
}

@Injectable()
export class AuthenticationService {
	// Constructor.
	constructor(
		private afAuth: AngularFireAuth,
		private http: HttpClient,
		protected router: Router,
		protected stompService: StompService,
		protected userService: UserService,
		private zkuService: ZkuService,
		private idWallService: IdWallService,
		private wePayOutService: WePayOutService,
		public cgiService: CGIService
	) {}

	// Admin Log in user.
	public adminLogin(
		email: string,
		password: string,
		professionalId: string
	): Observable<AdminLoginResponse> {
		// Body.
		const body = {
			email: email,
			password: password,
			userId: professionalId,
		};
		return this.http.post<AdminLoginResponse>('auth/adminlogin', body);
	}

	public checkBlacklist(email: string): Observable<boolean> {
		Sentry.configureScope((scope) => {
			scope.setUser({});
		});

		return this.http.get<boolean>(
			`accounts/validate-blacklist-professional/${email}`
		);
	}

	public logout(): void {
		Sentry.configureScope((scope) => {
			scope.setUser({});
		});

		// Remove caches.
		this.userService.setOnlineStatus(false).subscribe(() => {
			this.logoutServices().then(() => {
				this.router.navigate(['login'], {
					queryParamsHandling: 'preserve',
				});
			});
		});
	}

	// Log out user.
	public async logoutServices() {
		this.cgiService.baselineLevels = [];
		await Promise.all([
			this.afAuth.signOut(),
			// disconnect from rabbit
			this.stompService.disconnect(),
			this.idWallService.disconnect(),
			this.wePayOutService.disconnect(),
			// clear cache
			CacheService.getInstance().clearDatabase(),
			// Clear token and remove user from local storage to log user out.
			this.clearLocalStorage(),
		]);
	}

	// Remove stored items (Local storage).
	private clearLocalStorage(): void {
		localStorage.removeItem('dbEventsWeek');
		localStorage.removeItem('zenklubDb');
		localStorage.removeItem('zkcal');
		localStorage.removeItem('zkchat');
		localStorage.removeItem('zkconf');
		localStorage.removeItem('zkisvisible');
		localStorage.removeItem('zklastevup');
		localStorage.removeItem('zkmail');
		localStorage.removeItem('zkmobcals');
		localStorage.removeItem('zkmobLastMon');
		localStorage.removeItem('zkmobMaxD');
		localStorage.removeItem('zkmobMinD');
		localStorage.removeItem('zknewev');
		localStorage.removeItem('zkrefreshtoken');
		localStorage.removeItem('zksearch');
		localStorage.removeItem('zkstripestate');
		localStorage.removeItem('zktimezones');
		localStorage.removeItem('zkup');
		localStorage.removeItem('zkNotifications');

		let firebaseAuth: string;
		for (const i in localStorage) {
			if (localStorage.hasOwnProperty(i)) {
				if (i.match(/^firebase/g)) {
					firebaseAuth = i;
				}
			}
		}
		// just in case lets remove also firebase localStorage if exists
		if (firebaseAuth) {
			localStorage.removeItem(firebaseAuth);
		}

		this.zkuService.removeZku();
	}
}
